import React, { FC } from 'react';
import Active from 'assets/images/StatusIndicator/Active.svg';
import Inactive from 'assets/images/StatusIndicator/Inactive.svg';
import Unknown from 'assets/images/StatusIndicator/Unknown.svg';
import Box from '@mui/material/Box';
import AlertrSIM from 'assets/images/StatusIndicator/AlertrSIM.svg';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import { SESSION_STATUS_UI, SessionStatusUi } from '../utils/sessionStatus';
import { pxToRem } from 'src/common/utils/fonts';

type SessionStatusIndicatorProps = {
  status?: SessionStatusUi;
  paused?: boolean;
};

type FCWithChildren = React.FC<React.PropsWithChildren>;

const IconAlertBox: FCWithChildren = ({ children }) => (
  <Box
    sx={{
      position: 'absolute',
      top: pxToRem(-3),
      right: pxToRem(-3),
    }}
  >
    {children}
  </Box>
);

const IconRootBox: FCWithChildren = React.forwardRef(({ children, ...props }, ref) => (
  <Box ref={ref} sx={{ position: 'relative', width: pxToRem(24), height: pxToRem(24) }} {...props}>
    {children}
  </Box>
));

const AlertIcon = () => (
  <IconAlertBox>
    <AlertrSIM height={pxToRem(13)} data-testid="Pause icon" />
  </IconAlertBox>
);

export const SessionStatusIndicator: FC<SessionStatusIndicatorProps> = React.memo(
  ({ status, paused = false }) => {
    const { t } = useTranslation();
    const alertDecriptionRows = paused ? `${t('common.simulateOutageRunning')}!` : [];
    if (status === SESSION_STATUS_UI.IN_SESSION) {
      const label = t('common.inSession');
      return (
        <Tooltip title={label} description={alertDecriptionRows}>
          <IconRootBox>
            <Active
              width={pxToRem(24)}
              height={pxToRem(24)}
              aria-label={label}
              role="img"
              data-testid="sessionStatusActive"
            />
            {paused && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }

    if (status === SESSION_STATUS_UI.OUT_OF_SESSION) {
      const label = t('common.outSession');
      return (
        <Tooltip title={label} description={alertDecriptionRows}>
          <IconRootBox>
            <Inactive
              width={pxToRem(24)}
              height={pxToRem(24)}
              aria-label={label}
              role="img"
              data-testid="sessionStatusOutOfSession"
            />
            {paused && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }

    const label = t('common.unknown');
    return (
      <Tooltip title={label} description={alertDecriptionRows}>
        <IconRootBox>
          <Unknown
            width={pxToRem(24)}
            height={pxToRem(24)}
            aria-label={label}
            role="img"
            data-testid="sessionStatusUnknown"
          />
          {paused && <AlertIcon />}
        </IconRootBox>
      </Tooltip>
    );
  },
);
